import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { useWithdrawSowing } from "../../hooks/useSowings";
import { useNotification } from "@/shared/hooks/useNotification";
import type { Sowing } from "../../services/sowingsApi";
import { SowingContextBlock } from "./SowingContextBlock";

interface WithdrawSowingDialogProps {
  sowing: Sowing;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const WithdrawSowingDialog = ({
  sowing,
  open,
  onOpenChange,
  onSuccess,
}: WithdrawSowingDialogProps) => {
  const [reason, setReason] = useState("");
  const withdraw = useWithdrawSowing();
  const notify = useNotification();

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const handleSubmit = () => {
    if (!reason.trim()) {
      notify.error("Reason is required to withdraw a sowing.");
      return;
    }

    withdraw.mutate(
      {
        sowingId: sowing.id,
        sowingInstructionId: sowing.sowingInstructionId,
        payload: { reason },
      },
      {
        onSuccess: () => {
          notify.success("Sowing withdrawn.");
          onSuccess?.();
          onOpenChange(false);
        },
        onError: (error: any) => {
          notify.error(
            error.response?.data?.message || "Failed to withdraw sowing."
          );
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Withdraw Sowing</DialogTitle>
          <DialogDescription>
            Hard-deletes the recorded sowing event. Use only if the event did
            not actually happen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <SowingContextBlock sowing={sowing} />

          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-800">
              This action cannot be undone. {sowing.traysSown} trays will be
              removed from this instruction's recorded sowings, and the row
              will disappear from live queries. An audit row is preserved.
            </p>
          </div>

          <Field>
            <FieldLabel>
              Reason <span className="text-red-500">*</span>
            </FieldLabel>
            <Textarea
              placeholder="Why is this sowing being withdrawn?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={withdraw.isPending}
              maxLength={1000}
            />
          </Field>

          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={withdraw.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleSubmit}
              disabled={withdraw.isPending || !reason.trim()}
            >
              {withdraw.isPending ? "Withdrawing..." : "Withdraw"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
